import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  fetchVendorPoVendorSummary,
  previewVendorPurchaseOrders,
  sendVendorPurchaseOrdersBatch,
  PRICES_EXCLUDE_VAT,
  unpricedLineNote,
  vendorPoSendErrorMessage,
  type VendorPoSummary,
} from '../lib/vendorPurchaseOrderApi';
import { openPrintInNewTab } from '../lib/serverPrint';
import { formatDate as fmtDate } from "../utils/dateFormat";
import { formatCurrency } from "../utils/priceUtils";

interface VendorBatchSendModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SendResult {
  sent: number;
  skipped: number;
  failed: number;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The vendor-summary response carries no currency, so no label (a currency is
// never invented); rounded to the cent, not ceiled — the owner is
// BUSINESS_RULES#quotation-screen-money-labels.
const formatPoTotal = (value: number | null): string =>
  value === null || value === undefined ? '—' : formatCurrency(value, null, { ceil: false });

const vendorKey = (v: VendorPoSummary): string => v.vendorId || v.vendorName;

const VendorBatchSendModal: React.FC<VendorBatchSendModalProps> = ({ isOpen, onClose }) => {
  const [view, setView] = useState<'list' | 'review'>('list');
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState<VendorPoSummary[]>([]);

  const [selectedVendor, setSelectedVendor] = useState<VendorPoSummary | null>(null);
  const [format, setFormat] = useState<'pdf' | 'excel'>('pdf');
  const [recipient, setRecipient] = useState('');
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);

  const loadSummary = (): (() => void) => {
    let cancelled = false;
    setLoading(true);
    fetchVendorPoVendorSummary()
      .then((data) => {
        if (cancelled) return;
        setVendors(data || []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        window.alert(e instanceof Error ? e.message : 'Failed to load vendor summary');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  };

  useEffect(() => {
    if (!isOpen) return;
    setView('list');
    setVendors([]);
    setSelectedVendor(null);
    setResult(null);
    return loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const openReview = (v: VendorPoSummary) => {
    const validEmail = v.vendorEmail && v.vendorEmail !== '-' && EMAIL_RE.test(v.vendorEmail) ? v.vendorEmail : '';
    setSelectedVendor(v);
    setRecipient(validEmail);
    setFormat('pdf');
    setCheckedIds(new Set(v.pos.filter((po) => po.status !== 'sent' && po.status !== 'billed').map((po) => po.id)));
    setResult(null);
    setView('review');
  };

  const backToList = () => {
    setView('list');
    setSelectedVendor(null);
    setResult(null);
  };

  const toggleChecked = (id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const checkedIdList = Array.from(checkedIds);
  const anyChecked = checkedIdList.length > 0;
  const recipientValid = EMAIL_RE.test(recipient.trim());
  const willResend = !!selectedVendor && selectedVendor.pos.some((po) => checkedIds.has(po.id) && po.status === 'sent');
  // TECH_DEBT #342 — just the POs actually about to be sent this time, summed the
  // same way `sendVendorPurchaseOrdersBatch`'s groupUnpricedCount does server-side.
  const checkedUnpricedCount = selectedVendor
    ? selectedVendor.pos
        .filter((po) => checkedIds.has(po.id))
        .reduce((s, po) => s + (po.unpricedLineCount || 0), 0)
    : 0;

  const handlePreview = async () => {
    if (!anyChecked || previewing) return;
    setPreviewing(true);
    // D165: the tab opens inside the click; the server print takes a few seconds.
    await openPrintInNewTab(() => previewVendorPurchaseOrders(checkedIdList, format), {
      onBlocked: () => window.alert('Allow pop-ups for this site, then press Preview again.'),
      onError: (e) => window.alert(vendorPoSendErrorMessage(e, 'Failed to generate preview')),
    });
    setPreviewing(false);
  };

  const handleSend = async () => {
    if (!selectedVendor || !anyChecked || !recipientValid || sending) return;
    const trimmedRecipient = recipient.trim();
    if (!window.confirm(`Send ${checkedIdList.length} PO(s) to ${trimmedRecipient}?`)) return;
    setSending(true);
    try {
      const res = await sendVendorPurchaseOrdersBatch({
        poIds: checkedIdList,
        format,
        recipients: [trimmedRecipient],
        resend: willResend,
      });
      setResult({
        sent: res.sent?.length || 0,
        skipped: res.skipped?.length || 0,
        failed: res.failed?.length || 0,
      });
      setSelectedVendor((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pos: prev.pos.map((po) =>
            checkedIds.has(po.id) ? { ...po, status: 'sent', sentAt: new Date().toISOString() } : po
          ),
        };
      });
      loadSummary();
    } catch (e: unknown) {
      window.alert(vendorPoSendErrorMessage(e, 'Send failed'));
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const listBody = loading ? (
    <div className="flex items-center justify-center py-10 text-sm text-slate-500">Loading vendors…</div>
  ) : vendors.length === 0 ? (
    <div className="flex items-center justify-center py-10 text-sm text-slate-500">No vendors have any POs.</div>
  ) : (
    <div className="flex flex-col gap-2">
      {vendors.map((v) => {
        const key = vendorKey(v);
        return (
          <div
            key={key}
            className="flex items-center justify-between gap-3 p-3 border rounded-md"
            style={{ borderColor: '#D7DBD9' }}
          >
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-slate-900 text-sm font-medium truncate">{v.vendorName}</span>
              <span className="text-xs text-slate-500 truncate">
                {v.vendorEmail && v.vendorEmail !== '-' ? v.vendorEmail : <span className="text-amber-600">&#9888; no email</span>}
              </span>
              <span className="text-xs text-slate-500">
                {v.unsentCount} unsent &middot; {v.sentCount} sent
              </span>
              {/* TECH_DEBT #342 — summed across this vendor's whole group of POs
                  (server: unpricedLineCount), the same words the supplier-facing
                  PDF/Excel/email use, not a screen-local phrasing. */}
              {v.unpricedLineCount > 0 && (
                <span className="text-xs text-amber-700">{unpricedLineNote(v.unpricedLineCount)}</span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => openReview(v)}
                className="px-3 py-1.5 bg-primary-500 text-white rounded-md text-xs font-semibold hover:bg-primary-600 whitespace-nowrap"
              >
                Review &amp; Send
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const reviewBody = selectedVendor && (
    <>
      <div className="mb-4">
        <button
          type="button"
          onClick={backToList}
          className="text-xs font-semibold text-primary-500 hover:underline"
        >
          &larr; Back to vendors
        </button>
      </div>

      <h3 className="text-base font-semibold leading-6 text-slate-900 mb-4">
        Send to {selectedVendor.vendorName}
      </h3>

      <div className="mb-4">
        <label
          className="block mb-2 text-slate-900"
          style={{ fontFamily: 'Public Sans', fontWeight: 500, fontSize: '14px', lineHeight: '22px' }}
        >
          Recipient email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="vendor@supplier.com"
          className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white placeholder-[#999999] text-slate-900"
          style={{ fontFamily: 'Public Sans', fontWeight: 400, fontSize: '16px', lineHeight: '24px' }}
        />
        {recipient.trim() && !recipientValid && (
          <p className="mt-1 text-xs text-red-500">Enter a valid email address.</p>
        )}
      </div>

      <div className="mb-4">
        <label
          className="block mb-2 text-slate-900"
          style={{ fontFamily: 'Public Sans', fontWeight: 500, fontSize: '14px', lineHeight: '22px' }}
        >
          Format
        </label>
        <select
          value={format}
          onChange={(e) => setFormat(e.target.value as 'pdf' | 'excel')}
          className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white text-slate-900"
          style={{ fontFamily: 'Public Sans', fontWeight: 400, fontSize: '16px', lineHeight: '24px' }}
        >
          <option value="pdf">PDF</option>
          <option value="excel">Excel</option>
        </select>
      </div>

      <div className="mb-4">
        <label
          className="block mb-2 text-slate-900"
          style={{ fontFamily: 'Public Sans', fontWeight: 500, fontSize: '14px', lineHeight: '22px' }}
        >
          Purchase orders
        </label>
        <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto pr-1">
          {selectedVendor.pos.map((po) => {
            const isSent = po.status === 'sent';
            return (
              <label
                key={po.id}
                className="flex items-center gap-3 p-2 border rounded-md cursor-pointer"
                style={{ borderColor: '#D7DBD9' }}
              >
                <input
                  type="checkbox"
                  checked={checkedIds.has(po.id)}
                  onChange={() => toggleChecked(po.id)}
                />
                <span className="text-sm text-slate-900 flex-1 truncate">
                  {po.poNumber} &middot; {formatPoTotal(po.totalPrice)}
                  {/* D82: the wording the PO page, list and the supplier's PDF use. */}
                  {po.priceBasis === 'before_vat' && (
                    <span className="text-slate-500"> ({PRICES_EXCLUDE_VAT})</span>
                  )}
                  {/* TECH_DEBT #342 — same wording as the PDF/Excel/email this PO
                      is about to be sent as. */}
                  {po.unpricedLineCount > 0 && (
                    <span className="text-amber-700"> ({unpricedLineNote(po.unpricedLineCount)})</span>
                  )}
                </span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                    isSent ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {isSent && po.sentAt ? `Sent · ${fmtDate(po.sentAt)}` : isSent ? 'Sent' : 'Draft'}
                </span>
              </label>
            );
          })}
        </div>
        {checkedUnpricedCount > 0 && (
          <p className="mt-1.5 text-xs text-amber-700">
            The vendor's email will say the total {unpricedLineNote(checkedUnpricedCount)}.
          </p>
        )}
      </div>

      {result && (
        <div className="mb-4 text-xs text-green-700">
          Sent {result.sent}, skipped {result.skipped}, failed {result.failed}.
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={handlePreview}
          disabled={!anyChecked || previewing}
          className="flex-1 bg-white border border-primary-500 text-primary-500 rounded-lg flex items-center justify-center hover:bg-blue-50 transition-colors h-10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span style={{ fontFamily: 'Public Sans', fontWeight: 600, fontSize: '14px', lineHeight: '22px' }}>
            {previewing ? 'Preparing…' : 'Preview document'}
          </span>
        </button>
        <button
          type="button"
          onClick={handleSend}
          disabled={!anyChecked || !recipientValid || sending}
          className="flex-1 bg-primary-500 text-white rounded-lg flex items-center justify-center hover:bg-primary-600 transition-colors h-10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span style={{ fontFamily: 'Public Sans', fontWeight: 600, fontSize: '14px', lineHeight: '22px' }}>
            {sending ? 'Sending…' : willResend ? 'Resend selected' : 'Send selected'}
          </span>
        </button>
      </div>
    </>
  );

  const content = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 modal-overlay-safe">
      <div className="bg-white rounded-lg relative w-full max-w-[610px] max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 pb-4">
          <h2 className="text-base font-semibold leading-6 text-slate-900">
            Send by Vendor
          </h2>
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center bg-[#D9D9D9] rounded hover:bg-gray-400 transition-colors"
            aria-label="Close"
          >
            <span className="text-slate-400 text-lg leading-none">×</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 px-6 pb-4">{view === 'list' ? listBody : reviewBody}</div>

        {/* Footer */}
        {view === 'list' && (
          <div className="p-6 pt-0 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-white border border-[#D7DBD9] rounded-lg flex items-center justify-center hover:bg-slate-50 transition-colors h-10"
            >
              <span
                className="text-slate-900"
                style={{ fontFamily: 'Public Sans', fontWeight: 600, fontSize: '16px', lineHeight: '24px' }}
              >
                Close
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default VendorBatchSendModal;
